import { useEffect, useRef, useState } from 'react';
import { Search, Tag } from 'lucide-react';
import BlogCard from '../components/BlogCard';
import { getAllPosts } from '../lib/blog';
import type { BlogPostMetadata } from '../types/blog';

export default function HomePage() {
  const [posts, setPosts] = useState<BlogPostMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement | null>(null);
  const searchButtonRef = useRef<HTMLButtonElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    const loadPosts = async () => {
      try {
        const allPosts = await getAllPosts();
        setPosts(allPosts);
      } catch (error) {
        console.error('Error loading posts:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, []);

  useEffect(() => {
    if (showSearch) {
      searchInputRef.current?.focus();
    }
  }, [showSearch]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (
        showSearch &&
        searchContainerRef.current &&
        !searchContainerRef.current.contains(target) &&
        searchButtonRef.current &&
        !searchButtonRef.current.contains(target)
      ) {
        setShowSearch(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSearch]);

  // Add meta tags for SEO
  useEffect(() => {
    document.title = 'Blogg - Beredskapshubben';
    const canonicalUrl = 'https://blogg.beredskapshubben.se/';

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;
    
    const metaDescription = document.querySelector('meta[name="description"]');
    const description = 'Din källa för beredskapsguider, tips och råd om hemberedskap. Lär dig om förråd, vatten, checklista och mer på Beredskapshubben.';
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = description;
      document.head.appendChild(meta);
    }

    const allTags = [...new Set(posts.flatMap(p => p.tags))];
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    const keywords = allTags.join(', ');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', keywords);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = keywords;
      document.head.appendChild(meta);
    }
  }, [posts]);

  if (loading) {
    return (
      <div className="container mx-auto max-w-screen-xl px-4 py-12">
        <div className="flex items-center justify-center">
          <div className="text-center">
            <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-primary border-r-transparent"></div>
            <p className="text-muted-foreground">Laddar artiklar...</p>
          </div>
        </div>
      </div>
    );
  }

  const featuredPosts = posts.filter(post => post.featured);
  const normalize = (s: string) => s.toLowerCase();
  const filterBySearch = (post: BlogPostMetadata) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    const inTitle = post.title && normalize(post.title).includes(term);
    const inDesc = post.description && normalize(post.description).includes(term);
    const inTags = post.tags && post.tags.join(' ').toLowerCase().includes(term);
    return inTitle || inDesc || inTags;
  };
  const displayedFeatured = featuredPosts.filter(filterBySearch).reverse();
  const displayedFeaturedMobile = displayedFeatured.slice(0, 1); // Visa bara äldsta på mobil
  const displayedFeaturedDesktop = displayedFeatured.slice(0, 3); // Visa max 3 på desktop
  const displayedAll = posts.filter(filterBySearch);

  // Get latest post for visual highlight
  const latestPost = displayedAll.length > 0 ? displayedAll[0] : null;

  return (
    <div>
      {/* Schema.org structured data for Blog and ItemList */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          "name": "Blogg - Beredskapshubben - Sveriges beredskapsapp",
          "description": "Guider och tips för hemberedskap med Beredskaphubben.se.",
          "url": "https://blogg.beredskapshubben.se",
          "publisher": {
            "@type": "Organization",
            "name": "Beredskapshubben",
            "logo": {
              "@type": "ImageObject",
              "url": "https://blogg.beredskapshubben.se/icons/logo.svg"
            }
          },
          "blogPost": posts.map((post, index) => ({
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.description,
            "image": post.coverImage ? `https://blogg.beredskapshubben.se${post.coverImage}` : undefined,
            "author": {
              "@type": "Organization",
              "name": post.author || "Beredskapshubben"
            },
            "datePublished": post.publishedDate,
            "dateModified": post.updatedDate || post.publishedDate,
            "url": `https://blogg.beredskapshubben.se/post/${post.slug}`,
            "position": index + 1,
            "keywords": post.tags.join(", ")
          }))
        })}
      </script>

      {/* Content Section */}
      <div className="container mx-auto max-w-screen-xl px-4 pt-4 pb-8">

      {/* Main Layout with Sidebar on Desktop */}
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Main Content */}
        <div className="flex-1">
          {/* Featured Posts */}
          <section className="mb-12 md:pt-8">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-center text-3xl font-bold text-foreground flex-1">
                Utvalda artiklar
              </h2>
              {/* Search button - inline with heading */}
              <div className="relative" ref={searchContainerRef}>
                <button
                  ref={searchButtonRef}
                  type="button"
                  aria-label="Öppna sökfält"
                  onClick={() => setShowSearch((s) => !s)}
                  className="rounded p-2 text-muted-foreground hover:text-foreground focus:outline-none"
                >
                  <Search className="h-5 w-5" />
                </button>

                {showSearch && (
                  <div className="absolute right-0 top-full mt-2 z-20 w-72">
                    <input
                      ref={searchInputRef}
                      type="search"
                      aria-label="Sök artiklar"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Sök artiklar..."
                      className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                )}
              </div>
            </div>
            {displayedFeatured.length > 0 && (
              <div className="mx-auto max-w-5xl">
                {/* Mobile: Show only 1 (oldest) */}
                <div className="grid gap-8 md:hidden">
                  {displayedFeaturedMobile.map((post) => (
                    <BlogCard key={post.slug} post={post} />
                  ))}
                </div>
                {/* Desktop: Show up to 3 */}
                <div className="hidden gap-8 md:grid md:grid-cols-2 lg:grid-cols-3">
                  {displayedFeaturedDesktop.map((post) => (
                    <BlogCard key={post.slug} post={post} />
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Call to action - Beredskapshubben link */}
          <div className="mb-8 flex justify-center">
            <style>{`
              @keyframes glow-pulse {
                0%, 100% { box-shadow: 0 0 12px rgba(237,139,0,0.5); }
                50% { box-shadow: 0 0 20px rgba(237,139,0,0.8); }
              }
              .glow-pulse-btn {
                animation: glow-pulse 3s ease-in-out infinite;
              }
            `}</style>
            <a
              href="https://beredskapshubben.se"
              target="_blank"
              rel="noopener noreferrer"
              className="glow-pulse-btn inline-flex items-center gap-3 rounded-lg bg-[#1a1a1a]/80 backdrop-blur-sm border-2 border-[#ed8b00] px-6 py-3 text-base font-semibold text-[#e3ded5] transition-all hover:bg-[#2a2a2a]/90 hover:border-[#ffa726]"
            >
              <img src="/icons/logo.svg" alt="Beredskapshubben" className="h-6 w-6" />
              Till Beredskapshubben
            </a>
          </div>

          {/* All Posts */}
          {displayedAll.length > 0 && (
            <section>
              <h2 className="mb-6 text-center text-3xl font-bold text-foreground">
                Alla artiklar
              </h2>
              <div className="mx-auto max-w-5xl">
                <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                  {displayedAll.map((post) => (
                    <BlogCard 
                      key={post.slug} 
                      post={post} 
                      isLatest={latestPost !== null && post.slug === latestPost.slug}
                    />
                  ))}
                </div>
              </div>
            </section>
          )}

          {posts.length === 0 && (
            <div className="py-12 text-center">
              <p className="text-muted-foreground">Inga artiklar publicerade ännu. Kom tillbaka snart!</p>
            </div>
          )}
        </div>

        {/* Sidebar - Senaste inlägg (Desktop only) */}
        <aside className="hidden lg:block lg:w-72 flex-shrink-0 pl-8 pt-12">
          <div className="sticky top-6 space-y-8">
            {/* CTA card */}
            <div className="rounded-lg bg-[#282014]/90 backdrop-blur-sm border border-border/50 p-5">
              <h3 className="mb-2 text-base font-semibold text-[#e3ded5]">Är du redo?</h3>
              <p className="mb-4 text-sm text-[#e3ded5]">
                Börja din beredskapresa på beredskapshubben.se och förbered dig tillsammans med familj och grannar.
              </p>
              <a
                href="https://beredskapshubben.se"
                target="_blank"
                rel="noopener noreferrer"
                className="glow-pulse-btn inline-flex items-center gap-2 rounded-lg bg-[#1a1a1a]/80 backdrop-blur-sm border-2 border-[#ed8b00] px-4 py-2 text-sm font-semibold text-[#e3ded5] transition-all hover:bg-[#2a2a2a]/90 hover:border-[#ffa726]"
              >
                <img src="/icons/logo.svg" alt="Beredskapshubben" className="h-5 w-5" />
                Till Beredskapshubben
              </a>
            </div>

            {/* Tags */}
            <div className="mb-8">
              <h3 className="mb-4 text-lg font-semibold text-foreground">
                Taggar
              </h3>
              <div className="flex flex-wrap gap-2">
                {[...new Set(posts.flatMap(p => p.tags))].sort().map((tag) => (
                  <a
                    key={tag}
                    href={`/tag/${encodeURIComponent(tag)}`}
                    className="inline-flex items-center gap-1 rounded-md bg-secondary px-3 py-1 text-sm font-medium text-secondary-foreground transition-colors hover:bg-secondary/80"
                  >
                    <Tag className="h-3 w-3" />
                    {tag}
                  </a>
                ))}
              </div>
            </div>

            <h3 className="mb-4 text-lg font-semibold text-foreground">
              Senaste inlägg
            </h3>
            <div className="space-y-2">
              {posts.slice(0, 20).map((post) => (
                <a
                  key={post.slug}
                  href={`/post/${post.slug}`}
                  className="block rounded-md bg-card p-3 text-sm hover:bg-accent transition-colors"
                >
                  <div className="font-medium text-foreground line-clamp-2">
                    {post.title}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {new Date(post.publishedDate).toLocaleDateString('sv-SE', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </div>
                </a>
              ))}
            </div>
          </div>
        </aside>
      </div>
      </div>
    </div>
  );
}
